import type { QueryClient } from '@tanstack/react-query'
import type { HeadToHeadTab } from './head-to-head'
import type { WorldcupResearchSearch } from './worldcup-search'
import { stripSearchParams, useNavigate } from '@tanstack/react-router'
import {
  initialPublicResearchCoverageFilters,
  initialPublicResearchFacetFilters,
  initialPublicResearchFilters,
  nationalTeamProfileDefaultFilters,
  publicResearchCoverageQueryOptions,
  publicResearchFacetsQueryOptions,
  publicResearchHeadToHeadQueryOptions,
  publicResearchMatchDetailQueryOptions,
  publicResearchTeamMatchStatsQueryOptions,
  publicResearchTeamProfileQueryOptions,
} from '~/features/public-research/query-options'
import {
  EvidenceExplorer,
  GuidedWorldcupResearch,
  MatchDetailResearchView,
  NationalTeamResearchProfile,
  ResearchDiscoveryHome,
} from './components'
import { HeadToHeadResearchView } from './head-to-head'
import {
  worldcupResearchDefaultSearch,
  worldcupResearchSearchSchema,
} from './worldcup-search'

interface ResearchRouteHandle<TSearch = unknown, TParams = unknown> {
  fullPath: string
  useParams?: () => TParams
  useSearch?: () => TSearch
}

interface ResearchLoaderContext {
  queryClient: QueryClient
}

export const researchDiscoveryRouteKernel = {
  loader: ({ context }: { context: ResearchLoaderContext }) => {
    void context.queryClient.prefetchQuery(publicResearchCoverageQueryOptions({
      ...initialPublicResearchCoverageFilters,
      coverageLimit: 8,
      gapLimit: 4,
    }))
    void context.queryClient.prefetchQuery(publicResearchFacetsQueryOptions(initialPublicResearchFacetFilters))
  },
  component: ResearchDiscoveryHome,
}

export const worldcupResearchRouteKernel = {
  validateSearch: worldcupResearchSearchSchema.parse,
  search: {
    middlewares: [stripSearchParams(worldcupResearchDefaultSearch)] as never,
  },
  loader: ({ context, location }: { context: ResearchLoaderContext, location: { search: Record<string, unknown> } }) => {
    const search = worldcupResearchSearchSchema.parse(location.search)
    void context.queryClient.prefetchQuery(publicResearchFacetsQueryOptions({
      competitionCode: search.competitionCode,
      competitionCodes: search.competitionCodes,
      competitionName: search.competitionName,
      fromYear: search.fromYear,
      toYear: search.toYear,
      researchView: search.researchView,
      minDataQualityScore: search.minDataQualityScore,
    }))
    void context.queryClient.prefetchQuery(publicResearchTeamMatchStatsQueryOptions({ ...search, includeComparisonRows: true }))
  },
}

export function WorldcupResearchRouteKernel({ route }: { route: ResearchRouteHandle<WorldcupResearchSearch> }) {
  const search = route.useSearch?.() ?? worldcupResearchSearchSchema.parse({})
  const navigate = useNavigate({ from: route.fullPath as never })
  return (
    <GuidedWorldcupResearch
      search={search}
      onSearchChange={(nextSearch, options) => {
        void navigate({ search: () => worldcupResearchSearchSchema.parse(nextSearch), replace: options?.replace } as never)
      }}
    />
  )
}

export const headToHeadDefaultSearch = {
  team: undefined as string | undefined,
  opponent: undefined as string | undefined,
  tab: undefined as HeadToHeadTab | undefined,
}

export function parseHeadToHeadSearch(search: Record<string, unknown>): typeof headToHeadDefaultSearch {
  const tab: HeadToHeadTab | undefined = search.tab === 'direct' || search.tab === 'common-opponents' ? search.tab : undefined
  return {
    team: typeof search.team === 'string' ? search.team : undefined,
    opponent: typeof search.opponent === 'string' ? search.opponent : undefined,
    tab,
  }
}

export const headToHeadResearchRouteKernel = {
  validateSearch: parseHeadToHeadSearch,
  search: {
    middlewares: [stripSearchParams(headToHeadDefaultSearch)] as never,
  },
  loader: ({ context, location }: { context: ResearchLoaderContext, location: { search: Record<string, unknown> } }) => {
    const search = parseHeadToHeadSearch(location.search)
    const team = search.team ?? 'argentina'
    const opponent = search.opponent ?? 'france'
    void context.queryClient.prefetchQuery(publicResearchFacetsQueryOptions(initialPublicResearchFacetFilters))
    void context.queryClient.prefetchQuery(publicResearchHeadToHeadQueryOptions({
      ...initialPublicResearchFilters,
      team,
      opponent,
      limit: 100,
    }))
  },
}

export function HeadToHeadResearchRouteKernel({ route }: { route: ResearchRouteHandle<typeof headToHeadDefaultSearch> }) {
  const search = route.useSearch?.() ?? headToHeadDefaultSearch
  const navigate = useNavigate({ from: route.fullPath as never })
  return (
    <HeadToHeadResearchView
      initialTeam={search.team ?? 'argentina'}
      initialOpponent={search.opponent ?? 'france'}
      initialTab={search.tab}
      onTabChange={(tab) => {
        void navigate({ search: (current: Record<string, unknown>) => parseHeadToHeadSearch({ ...current, tab }) } as never)
      }}
    />
  )
}

export const matchDetailResearchRouteKernel = {
  loader: ({ context, params }: { context: ResearchLoaderContext, params: { matchId: string } }) => {
    void context.queryClient.prefetchQuery(publicResearchMatchDetailQueryOptions(params.matchId))
  },
}

export function MatchDetailResearchRouteKernel({ route }: { route: ResearchRouteHandle<unknown, { matchId: string }> }) {
  const { matchId } = route.useParams?.() ?? { matchId: '' }
  return <MatchDetailResearchView matchId={matchId} />
}

export const teamProfileResearchRouteKernel = {
  loader: ({ context, params }: { context: ResearchLoaderContext, params: { team: string } }) => {
    void context.queryClient.prefetchQuery(publicResearchTeamProfileQueryOptions(params.team, {
      ...nationalTeamProfileDefaultFilters,
    }))
  },
}

export function TeamProfileResearchRouteKernel({ route }: { route: ResearchRouteHandle<unknown, { team: string }> }) {
  const { team } = route.useParams?.() ?? { team: '' }
  return <NationalTeamResearchProfile teamSlug={team} />
}

export const evidenceExplorerRouteKernel = {
  validateSearch: parseEvidenceExplorerSearch,
  component: EvidenceExplorer,
}

export function parseEvidenceExplorerSearch(search: Record<string, unknown>) {
  return {
    intent: typeof search.intent === 'string' ? search.intent : undefined,
    team: typeof search.team === 'string' ? search.team : undefined,
    secondTeam: typeof search.secondTeam === 'string' ? search.secondTeam : undefined,
    opponent: typeof search.opponent === 'string' ? search.opponent : undefined,
    competitionCode: typeof search.competitionCode === 'string' ? search.competitionCode : undefined,
    competitionName: typeof search.competitionName === 'string' ? search.competitionName : undefined,
    season: typeof search.season === 'string' || typeof search.season === 'number' ? search.season : undefined,
  }
}
